/** @format */

import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { getUserToken, setUserToken } from "../authSlice";
import { fetchTokenWithCode, fetchTokenWithRefreshToken } from "./hwbService";
import { useTranslation } from "react-i18next";
import { Spinner } from "react-bootstrap";
import { Buffer } from "buffer";
import ErrorPage from "../../errorPage/ErrorPage";

const HwbLogin = () => {
  const { search, state } = useLocation();
  const dispatch = useAppDispatch();
  const token = useAppSelector((state) => getUserToken(state));
  const [code, setCode] = useState<string>();
  const [redirect, setRedirect] = useState<string>();
  const [error, setError] = useState<{ status: number; message?: string }>();
  const { t: _ } = useTranslation();
  const [searchParams] = useSearchParams();

  // Store the location to redirect to
  useEffect(() => {
    if (state?.from?.pathname) {
      console.log(state.from.pathname);
      localStorage.setItem("redirect_uri", state.from.pathname);
    } else {
      if (token) {
        setRedirect(localStorage.getItem("redirect_uri") ?? "/");
        redirect && localStorage.removeItem("redirect_uri");
      }
    }
  }, [state, token, redirect]);

  // if a token exists, send it back to the redirect
  useEffect(() => {
    if (token && redirect) {
      // token is in date, use this
      if (token.expires > Date.now()) {
        window.location.href = `${redirect}?tkid=${Buffer.from(
          JSON.stringify(token),
          "utf-8",
        ).toString("base64")}`;
        return;
      }
      // token has expired, refresh
      fetchTokenWithRefreshToken(token.refreshToken)
        .then((userToken) => {
          if (userToken !== undefined) {
            localStorage.setItem("BL_CMS_STATE", JSON.stringify({ userToken: userToken}));
            window.location.href = `${redirect}?tkid=${Buffer.from(
              JSON.stringify(userToken),
              "utf-8",
            ).toString("base64")}`;
          } else {
            // clear token and error if none can be requested
            dispatch(setUserToken(undefined));
            setError({ status: 500, message: _("login_500") });
            return;
          }
        })
        .catch((error) => {
          setError({
            status: error.status,
            message: error.status === 403 ? _("login_403") : _("login_500"),
          });
          return;
        });
    }
  }, [redirect, token, _, dispatch]);

  // if no token exists, redirect to get a new token
  useEffect(() => {
    if (token) return;

    const code = searchParams.get("code");
    if (!code) {
      const endpoint = `${
        process.env.REACT_APP_RESOURCES_API_URL
      }hwbauth/login?domain=${encodeURIComponent(window.location.origin)}`;
      window.location.replace(endpoint);
    } else {
      setCode(code);
    }
  }, [code, search, searchParams, token]);

  // if a code exists, get a token using it
  useEffect(() => {
    if (!code || token) return;

    fetchTokenWithCode(code)
      .then((userToken) => {
        if (userToken) dispatch(setUserToken(userToken));
      })
      .catch((error) => {
        setError({
          status: error.status,
          message: error.status === 403 ? _("login_403") : _("login_500"),
        });
      });
  }, [_, code, dispatch, token]);

  return error ? (
    <ErrorPage
      statusCode={
        "status" in error && typeof error.status === "number"
          ? error.status
          : undefined
      }
      hiddenMessage={
        "data" in error && typeof error.data === "string"
          ? error.data
          : undefined
      }
      actions={["report", "newResource"]}
    />
  ) : token ? (
    <div className="d-flex justify-content-center m-2">
      <Spinner />
    </div>
  ) : (
    <p>{_("Loading")}...</p>
  );
};

export default HwbLogin;
